import {
  Autocomplete,
  Button,
  CheckboxField,
  Divider,
  Flex,
  Grid,
  Input,
  Label,
  SelectField,
  TextField,
} from "@aws-amplify/ui-react";
import { API, graphqlOperation } from "aws-amplify";
import moment from "moment";
import { useContext, useEffect, useRef, useState } from "react";
import LocalizedStrings from "react-localization";
import toast from "react-hot-toast";

import { globalStrings } from "config/translations/es";
import { listCompanies } from "graphql/queries";
import LabelRequired from "components/utils/LabelRequired";
import useErrors from "hooks/useErrors";
import InputList from "components/utils/InputList";
import PhoneList from "components/phone/PhoneList";
import RolesChoose from "components/roles/RolesChoose";
import { LayoutContext } from "layout/context/LayoutContext";

const UserForm = ({
  onSubmit,
  type = "create",
  initialValues,
  profile = false,
  initialPhotoSrc,
}) => {
  const { me } = useContext(LayoutContext);
  const [userForm, setUserForm] = useState(initialValues);
  const fileInputRef = useRef(null);
  const [companies, setCompanies] = useState([]);
  const [photoSrc, setPhotoSrc] = useState(initialPhotoSrc);
  const [resetValues, setResetValues] = useState(false);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [errors, setErrors] = useState({
    companyID: "",
  });
  const validateErrors = useErrors({
    companyID: "isRequired",
  });
  const validateListErrors = useErrors({
    phone: "isPhone",
    email: "isEmail",
  });

  const checkErrors = (name, value) => {
    if (name === "companyID" && (!!userForm.isSuper || profile)) return null;
    const errorMessage = validateErrors(name, value);
    if (errorMessage)
      setErrors((prevErr) => ({ ...prevErr, [name]: errorMessage }));
    else setErrors((prevErr) => ({ ...prevErr, [name]: "" }));
    return errorMessage;
  };

  const onSubmitForm = async () => {
    let hasErrors = false;

    Object.entries(userForm).forEach((itemForm) => {
      const [name, value] = itemForm;
      const check = !!checkErrors(name, value);
      if (!hasErrors) hasErrors = check;
    });

    if (userForm.phones?.some((ph) => !!validateListErrors("phone", ph))) {
      toast.error(strings.phones.invalid);
      return;
    }

    if (
      userForm.additionalEmails?.some(
        (email) => !!validateListErrors("email", email)
      )
    ) {
      toast.error(strings.additionalEmails.invalid);
      return;
    }

    if (
      !profile &&
      userForm.companyID &&
      !companies.some((companyRec) => companyRec.id === userForm.companyID)
    ) {
      setErrors((prevSt) => ({
        ...prevSt,
        companyID: strings.validations.incorrectValue,
      }));
      return;
    }

    if (!hasErrors) {
      if (!profile)
        onSubmit({
          ...userForm,
          phones: userForm.phones?.filter((ph) => !!ph),
          additionalEmails: userForm.additionalEmails?.filter((em) => !!em),
          addresses: userForm.addresses?.filter((addr) => !!addr),
          companyID: userForm.companyID || null,
        });
      else
        onSubmit({
          ...userForm,
          phones: userForm.phones?.filter((ph) => !!ph),
          additionalEmails: userForm.additionalEmails?.filter((em) => !!em),
          addresses: userForm.addresses?.filter((addr) => !!addr),
        });
    }
  };

  const onPhotoClick = () => {
    fileInputRef.current.click();
  };

  const onChange = (e) => {
    const { name, value, checked } = e.target;
    setUserForm((prevSt) => ({
      ...prevSt,
      [name]: name === "isSuper" ? checked : value,
    }));
    checkErrors(name, value);
    if (name === "isSuper")
      setUserForm((prevSt) => ({
        ...prevSt,
        companyID: null,
      }));
  };

  const onChangePhoto = (event) => {
    const photo = event.target.files[0];
    setUserForm((prevSt) => ({
      ...prevSt,
      photo,
    }));
  };

  const onChangeList = (name, list) => {
    setUserForm((prevSt) => ({
      ...prevSt,
      [name]: list ?? [],
    }));
  };

  const onChangeSelect = (e) => {
    const { name, value } = e.target;
    if (name === "companyID") {
      fetchCompanies({ name: value });
    }
    setUserForm((prevSt) => ({
      ...prevSt,
      [name]: value,
    }));
    checkErrors(name, value);
  };

  const onSelect = (name, value) => {
    setUserForm((prevSt) => ({
      ...prevSt,
      [name]: value,
    }));
    checkErrors(name, value);
  };

  const onClearSelect = (name) => {
    const value = "";
    if (name === "companyID") {
      fetchCompanies({ name: value });
    }
    setUserForm((prevSt) => ({
      ...prevSt,
      [name]: value,
    }));
  };

  const resetFormValues = () => {
    setErrors({
      companyID: "",
    });
    setUserForm(initialValues);
    fetchCompanies({ id: initialValues.companyID });
    setResetValues((prev) => !prev);
  };

  async function fetchCompanies({ id = "", name = "" }) {
    let filterObj = {};
    try {
      setCompaniesLoading(true);

      if (me.companyID)
        filterObj = { ...filterObj, or: [{ id: { eq: me.companyID } }] };
      if (me.managedCompanies.length > 0)
        filterObj = {
          ...filterObj,
          or: [
            ...(filterObj?.or ?? []),
            ...me.managedCompanies.map((cp) => ({ id: { eq: cp } })),
          ],
        };

      const companiesData = await API.graphql(
        graphqlOperation(listCompanies, {
          limit:
            !id && !name && Object.keys(filterObj).length <= 0 ? 20 : undefined,
          filter: {
            and: [
              {
                id: { contains: id ?? "" },
              },
              {
                name: { contains: name ?? "" },
              },
            ],
            ...filterObj,
          },
        })
      );
      const companiesItems = companiesData.data.listCompanies.items;
      setCompanies(companiesItems);
      setCompaniesLoading(false);
    } catch (err) {
      toast.error(strings.responses.errorFetchCompanies);
    }
  }

  useEffect(() => {
    setUserForm(initialValues);
    if (!profile) fetchCompanies({ id: initialValues.companyID });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues, profile]);

  useEffect(() => {
    if (userForm.photo) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoSrc(reader.result);
      };
      reader.readAsDataURL(userForm.photo);
    }
  }, [userForm.photo]);

  return (
    <Grid padding="20px" rowGap="15px">
      {profile && (
        <Grid
          templateColumns={{ base: "1fr", large: "1fr 1fr 1fr" }}
          rowGap="15px"
          columnGap="15px"
        >
          <Flex
            columnStart={2}
            columnEnd={3}
            direction="column"
            alignItems="center"
          >
            <img
              src={photoSrc}
              alt="photo_profile"
              className="rounded"
              style={{ width: "150px", height: "auto" }}
            />
            <Button size="small" onClick={onPhotoClick} variation="primary">
              {strings.uploadPhoto}
            </Button>
            <Input
              name="photo"
              label={strings.fields.photo.label}
              type="file"
              accept="image/*"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={onChangePhoto}
            ></Input>
          </Flex>
        </Grid>
      )}
      <Grid
        templateColumns={{ base: "1fr", large: "1fr 1fr 1fr" }}
        rowGap="15px"
        columnGap="15px"
      >
        <TextField
          columnSpan={profile ? 3 : 1}
          name="email"
          label={<LabelRequired text={strings.fields.email.label} />}
          value={userForm.email}
          disabled
        ></TextField>
        {!userForm.isSuper && !me.companyID && !profile && (
          <Flex direction="column" gap="8px">
            <Label htmlFor="companyID">
              {<LabelRequired text={strings.fields.company.label} />}
            </Label>
            <Autocomplete
              id="companyID"
              name="companyID"
              label={strings.fields.company.label}
              isRequired
              placeholder={strings.fields.company.placeholder}
              value={
                companies.find(
                  (companyRec) => companyRec.id === userForm.companyID
                )?.name ??
                userForm.companyID ??
                ""
              }
              options={companies.map((companyRec) => ({
                id: companyRec.id,
                label: companyRec.name,
              }))}
              isLoading={companiesLoading}
              onClear={() => onClearSelect("companyID")}
              onChange={onChangeSelect}
              onSelect={({ id }) => onSelect("companyID", id)}
              errorMessage={errors.companyID}
              hasError={!!errors.companyID}
              menuSlots={{ Empty: globalStrings.noResults }}
            ></Autocomplete>
          </Flex>
        )}
        {!profile && !me.companyID && (
          <CheckboxField
            name="isSuper"
            label={strings.fields.isSuper.label}
            checked={userForm.isSuper}
            onChange={onChange}
          ></CheckboxField>
        )}
      </Grid>
      <Grid
        templateColumns={{ base: "1fr", large: "1fr 1fr 1fr" }}
        rowGap="15px"
        columnGap="15px"
      >
        <TextField
          name="name"
          label={strings.fields.name.label}
          value={userForm.name || ""}
          onChange={onChange}
        ></TextField>
        <Flex direction="column" gap="8px">
          <Label htmlFor="companyID">{strings.fields.birthDate.label}</Label>
          <Input
            name="birthDate"
            label={strings.fields.birthDate.label}
            value={userForm.birthDate || ""}
            type="date"
            max={moment().format("YYYY-MM-DD")}
            onChange={onChange}
          ></Input>
        </Flex>
        <TextField
          name="position"
          label={strings.fields.position.label}
          value={userForm.position || ""}
          onChange={onChange}
        ></TextField>
        <SelectField
          name="gender"
          value={userForm.gender || ""}
          label={strings.fields.gender.label}
          onChange={onChange}
        >
          <option value="">{globalStrings.selectOption}</option>
          <option value="H">{strings.fields.gender.H}</option>
          <option value="M">{strings.fields.gender.M}</option>
          <option value="O">{strings.fields.gender.O}</option>
        </SelectField>
      </Grid>
      <Divider className="mt-1 mb-2" />
      <PhoneList
        title={strings.phones.title}
        values={userForm.phones ?? []}
        onChange={(values) => onChangeList("phones", values)}
      />
      <Divider className="mt-1 mb-2" />
      <InputList
        title={strings.additionalEmails.title}
        values={userForm.additionalEmails ?? []}
        onChange={(values) => onChangeList("additionalEmails", values)}
        inputPlaceholder={strings.additionalEmails.inputPlaceholder}
        inputType="email"
      />
      <Divider className="mt-1 mb-2" />
      <InputList
        title={strings.addresses.title}
        values={userForm.addresses ?? []}
        onChange={(values) => onChangeList("addresses", values)}
        inputPlaceholder={strings.addresses.inputPlaceholder}
        inputType="text"
      />
      {!profile && (
        <>
          <Divider className="mt-3 mb-2" />
          <RolesChoose
            initRolesGranted={initialValues.roles ?? []}
            onChangeRolesGranted={(values) => onChangeList("roles", values)}
            resetValues={resetValues}
          />
        </>
      )}
      <Flex justifyContent="end">
        <Button
          children={strings.fields.buttons.reset}
          onClick={resetFormValues}
        ></Button>
        <Button
          children={
            type === "create"
              ? strings.fields.buttons.create
              : strings.fields.buttons.save
          }
          onClick={onSubmitForm}
          variation="primary"
          isDisabled={Object.values(errors).some((e) => !!e)}
        ></Button>
      </Flex>
    </Grid>
  );
};

const strings = new LocalizedStrings({
  es: {
    uploadPhoto: "Subir Foto",
    fields: {
      email: {
        label: "Correo electrónico",
      },
      name: {
        label: "Nombre",
      },
      position: {
        label: "Cargo",
      },
      birthDate: {
        label: "Fecha de nacimiento",
      },
      photo: {
        label: "Foto de Perfil",
      },
      gender: {
        label: "Género",
        H: "Hombre",
        M: "Mujer",
        O: "Otro",
      },
      company: {
        label: "Empresa",
        placeholder: "Buscar empresa...",
      },
      isSuper: {
        label: "Super Administrador",
      },
      buttons: {
        create: "Crear",
        save: "Guardar",
        reset: "Restablecer",
      },
    },
    validations: {
      incorrectValue: "Valor incorrecto",
    },
    responses: {
      errorFetchCompanies: "Error al consultar empresas",
    },
    additionalEmails: {
      title: "Correos adicionales",
      inputPlaceholder: "Ingrese correo",
      invalid: "Correos inválidos",
    },
    addresses: {
      title: "Direcciones",
      inputPlaceholder: "Ingrese dirección",
    },
    phones: {
      title: "Teléfonos",
      invalid: "Teléfonos inválidos",
    },
  },
});

export default UserForm;
